<template>
  <div>
    <div
      v-if="activeJournalCategory !== null"
      class="journal-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <header class="journal-overlay__header">
        <h5 class="journal-overlay__title">
          {{ title }}
        </h5>

        <div class="journal-overlay__actions">
          <button
            type="button"
            @click="print"
          >
            <img
              src="../assets/icons/printer.svg"
              alt
              class="icon"
            >
          </button>
          <button
            type="button"
            aria-label="Close journal"
            @click="dismissJournal"
          >
            <img
              src="../assets/icons/close.svg"
              alt
              class="icon"
            >
          </button>
        </div>
      </header>

      <div class="journal-overlay__body">
        <div
          v-if="status == 'loading'"
          class="loading"
        >
          Loading <spinner color="var(--primary)" />
        </div>
        <template v-else-if="status == 'loaded'">
          <template v-if="entries.length">
            <div
              v-for="(entry, index) of entries"
              :key="`entry${index}`"
              class="entry"
            >
              <div class="entry-top">
                <h6>{{ entry.label }}</h6>

                <span class="entry-date">
                  {{ formatTimestamp(entry.timestamp) }}
                </span>
              </div>

              <div v-html="linebreaksbr(entry.user_response)" />
            </div>
          </template>
          <div
            v-else
            class="no-entries"
          >
            You do not have any entries in this portion of your journal yet. As
            you progress through the program, entries will
            automatically appear here.
          </div>
        </template>
        <div
          v-else-if="status == 'error'"
          class="error"
        >
          <div
            class="alert alert-danger"
            role="alert"
          >
            <p>
              Unable to communicate with the server. Please check your connection
              and try again.
            </p>

            <button
              type="button"
              class="btn btn-light"
              @click="loadData"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';
import Spinner from '@/components/Spinner.vue';

import { COMPACT_MOBILE_MEDIA } from '@/utils/media';

const MOBILE_MEDIA = COMPACT_MOBILE_MEDIA;

export default {
  name: 'Journal',
  components: {
    Spinner,
  },
  data() {
    return {
      status: 'loading',
      title: '',
      entries: [],
      slugMap: {
        belief: 'Belief',
        essence: 'Essence',
        how: 'The How',
        target: 'The Target',
      },
      isMobile: false,
      journalHistoryPushed: false,
    };
  },
  computed: {
    activeJournalCategory() {
      return this.$store.state.activeJournalCategory;
    },
    activeJournalProgram() {
      return this.$store.state.activeJournalProgram;
    },
    userData() {
      return this.$store.state.userData;
    },
  },
  watch: {
    activeJournalCategory(value, oldValue) {
      if (value !== null) {
        this.title = `Your Journal: ${this.slugMap[value]}`;
        this.loadData();
        this.pushJournalHistory();
      } else if (oldValue !== null) {
        if (this.journalHistoryPushed) {
          this.journalHistoryPushed = false;
          window.history.back();
        }
      }
    },
  },
  mounted() {
    this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA);
    this.isMobile = this.mobileMediaQuery.matches;
    this.onMobileMediaChange = (event) => {
      this.isMobile = event.matches;
    };
    this.mobileMediaQuery.addEventListener('change', this.onMobileMediaChange);
    window.addEventListener('popstate', this.onJournalPopState);
  },
  beforeDestroy() {
    if (this.mobileMediaQuery && this.onMobileMediaChange) {
      this.mobileMediaQuery.removeEventListener('change', this.onMobileMediaChange);
    }
    window.removeEventListener('popstate', this.onJournalPopState);
  },
  methods: {
    useJournalHistory() {
      return this.isMobile || Capacitor.isNativePlatform();
    },
    pushJournalHistory() {
      if (!this.useJournalHistory() || this.journalHistoryPushed) {
        return;
      }
      window.history.pushState({ journalOverlay: true }, '');
      this.journalHistoryPushed = true;
    },
    dismissJournal() {
      this.$store.commit('activeJournalCategory', null);
      this.$store.commit('activeJournalProgram', null);
    },
    onJournalPopState() {
      if (!this.journalHistoryPushed) {
        return;
      }
      this.journalHistoryPushed = false;
      if (this.activeJournalCategory !== null) {
        this.$store.commit('activeJournalCategory', null);
        this.$store.commit('activeJournalProgram', null);
      }
    },
    formatTimestamp(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    async loadData() {
      this.status = 'loading';

      const response = await fetch(
        `${process.env.VUE_APP_API_URL}programs/8_weeks_to_be/journal/?category=${this.activeJournalCategory}&program=${this.activeJournalProgram}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${this.userData.drf_token}`,
          },
        },
      );

      if (response && [200, 401].includes(response.status)) {
        if (response.status === 200) {
          this.entries = await response.json();
          this.status = 'loaded';
        } else {
          this.logUserOut();
        }
      } else {
        this.status = 'error';
      }
    },
    print() {
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
.journal-overlay__header button {
  border: 0;
  background-color: transparent;
  padding: 0;

  + button {
    margin-left: 1rem;

    .icon {
      width: 14px;
    }
  }
}

.loading,
.error,
.no-entries {
  padding: 2.5rem;
}

.loading {
  text-align: center;
}

.error .alert {
  margin-bottom: 0;
}

.entry {
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  + .entry {
    border-top: 1px solid #e6e6e6;
  }
}

.entry-top {
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: 1fr auto;
}

.entry-date {
  font-size: 0.8rem;
}

@media screen {
  .entry {
    padding-right: 2.5rem;
    padding-left: 2.5rem;
  }

  .entry-date {
    color: #c9c9c9;
  }
}
</style>

<style lang="scss">
@import '@/styles/breakpoints.scss';

.journal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  background: var(--app-gradient);
}

.journal-overlay__header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  height: var(--mobile-header-total);
  padding-top: var(--safe-area-top);
  padding-right: max(15px, var(--safe-area-right));
  padding-left: max(15px, var(--safe-area-left));
  background: var(--app-gradient);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.journal-overlay__title {
  margin: 0;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.2;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.15);
}

.journal-overlay__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
  }
}

.journal-overlay__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: #fff;
  border-radius: 3px 3px 0 0;
  box-shadow: 0 -0.5rem 1rem rgba(0, 0, 0, 0.08);
  font-size: 0.875rem;
  word-break: break-word;
}

@media (min-width: 568px) {
  .journal-overlay {
    padding: 2rem;
    align-items: center;
    justify-content: center;
    background: rgba(33, 37, 41, 0.55);
  }

  .journal-overlay__header,
  .journal-overlay__body {
    width: 100%;
    max-width: 720px;
  }

  .journal-overlay__body {
    border-radius: 0 0 3px 3px;
    max-height: calc(100vh - 6rem);
  }
}

// Phone landscape is >568px wide — use full-screen journal, not a letterboxed card.
@media #{$phone-landscape} {
  .journal-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: flex-start;
    background: #fff;
  }

  .journal-overlay__header {
    width: 100%;
    max-width: none;
    padding-top: max(4px, env(safe-area-inset-top, 0px));
    padding-left: max(20px, env(safe-area-inset-left, 0px), var(--landscape-notch-left, 0px));
    padding-right: max(20px, env(safe-area-inset-right, 0px), var(--landscape-notch-right, 0px));
    background: var(--app-gradient);
  }

  .journal-overlay__body {
    width: 100%;
    max-width: none;
    flex: 1;
    min-height: 0;
    border-radius: 0;
    max-height: none;
    padding-left: max(20px, env(safe-area-inset-left, 0px), var(--landscape-notch-left, 0px));
    padding-right: max(20px, env(safe-area-inset-right, 0px), var(--landscape-notch-right, 0px));
    box-sizing: border-box;
  }
}

@media print {
  .journal-overlay {
    position: static;
    background: none;
    display: block;
  }

  .journal-overlay__header {
    padding-top: 0;

    button {
      display: none;
    }
  }

  .journal-overlay__title {
    color: #424140;
    text-shadow: none;
  }

  .journal-overlay__body {
    box-shadow: none;
    max-height: none;
  }
}
</style>
